import React, { useState, useEffect, useRef, useMemo } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { useSearchParams, useNavigate } from "react-router-dom";
import { auth, db, storage } from "./firebase";
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  getDocs,
  where,
  limit,
  doc,
  getDoc,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import dayjs from "dayjs";

// Clean WhatsApp-style floating date chips
const DateChip = ({ ts }) => {
  if (!ts) return null;
  return (
    <div className="my-4 flex justify-center sticky top-2 z-20">
      <span className="text-[11px] px-3 py-1 rounded-md bg-white text-gray-500 shadow-sm border border-gray-100 uppercase tracking-wide font-medium">
        {dayjs(ts).format("MMMM D, YYYY")}
      </span>
    </div>
  );
};

// Premium WhatsApp-style chat bubbles with light-green/white themes and micro-checkmarks
const ChatBubble = ({ msg, isMine, time, unread }) => {
  const bubbleBase =
    "relative max-w-[75%] px-3 py-1.5 rounded-lg shadow-sm break-words text-[14.5px] leading-relaxed mb-1 selection:bg-emerald-200";
  const mineStyle = "bg-[#d9fdd3] text-gray-900 rounded-tr-none";
  const theirStyle = "bg-white text-gray-900 rounded-tl-none";

  return (
    <div className={`flex w-full ${isMine ? "justify-end" : "justify-start"}`}>
      <div className={`${bubbleBase} ${isMine ? mineStyle : theirStyle}`}>
        
        {/* Message Core Text */}
        {msg.text && <p className="whitespace-pre-wrap pr-12 pb-1">{msg.text}</p>}

        {/* Dynamic Image Attachments */}
        {msg.fileUrl && msg.fileType === "image" && (
          <div className="mt-1 mb-1 max-w-[280px] rounded-md overflow-hidden border border-black/5 bg-gray-50">
            <img src={msg.fileUrl} className="w-full object-cover" alt="attachment" />
          </div>
        )}
        
        {/* Dynamic Document/File Attachments */}
        {msg.fileUrl && msg.fileType === "file" && (
          <a
            href={msg.fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-2 mt-1 mb-1 p-2 rounded-md text-xs border transition-all ${
              isMine 
                ? "text-emerald-800 bg-emerald-600/10 border-emerald-600/20 hover:bg-emerald-600/20" 
                : "text-gray-800 bg-gray-100 border-gray-200 hover:bg-gray-200"
            }`}
          >
            📄 View Shared Document
          </a>
        )}

        {/* Micro-Metadata Layer (Time + Double Checkmarks) */}
        <div className="absolute bottom-1 right-2 text-[10px] text-gray-400 select-none flex items-center space-x-1">
          <span>{time}</span>
          {isMine && (
            <span className={unread ? "text-gray-400" : "text-[#53bdeb] text-xs font-bold"}>
              ✓✓
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const Inbox = () => {
  const [user] = useAuthState(auth);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const partnerId = searchParams.get("user");

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [chatList, setChatList] = useState([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [activeLastReadAt, setActiveLastReadAt] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [partnerName, setPartnerName] = useState("");
  const [searchQuery, setSearchQuery] = useState(""); // Native Search Input State

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const chatId = user && partnerId ? [user.uid, partnerId].sort().join("_") : null;

  const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
  const purgeOldMessages = async (ci) => {
    if (!ci) return;
    try {
      const cutoff = new Date(Date.now() - THIRTY_DAYS_MS);
      const msgsCol = collection(db, "chats", ci, "messages");
      while (true) {
        const snap = await getDocs(
          query(msgsCol, orderBy("createdAt"), where("createdAt", "<", cutoff), limit(300))
        );
        if (snap.empty) break;
        const batch = writeBatch(db);
        snap.docs.forEach((docSnap) => batch.delete(docSnap.ref));
        await batch.commit();
        if (snap.size < 300) break;
      }
    } catch (e) {
      console.warn("Purge old messages failed for", ci, e);
    }
  };

  useEffect(() => {
    const fetchPartnerName = async () => {
      if (!partnerId) {
        setPartnerName("");
        return;
      }
      try {
        const snap = await getDoc(doc(db, "users", partnerId));
        if (snap.exists()) {
          const d = snap.data();
          setPartnerName(d.firstName || d.fullName || partnerId);
        } else {
          setPartnerName(partnerId);
        }
      } catch {
        setPartnerName(partnerId || "");
      }
    };
    fetchPartnerName();
  }, [partnerId]);

  useEffect(() => {
    if (!user) return;
    const loadRole = async () => {
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (snap.exists()) {
          setUserRole(snap.data().role || null);
        }
      } catch (err) {
        console.error("Failed to fetch user role", err);
      }
    };
    loadRole();
  }, [user?.uid]);

  useEffect(() => {
    if (!user || !partnerId || !chatId) return;
    purgeOldMessages(chatId);
    const msgsRef = collection(db, "chats", chatId, "messages");
    const qMsgs = query(msgsRef, orderBy("createdAt", "asc"));
    const unsubscribe = onSnapshot(qMsgs, (snapshot) => {
      const msgs = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setMessages(msgs);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    });
    return () => unsubscribe();
  }, [user?.uid, partnerId, chatId]);

  useEffect(() => {
    const loadLastRead = async () => {
      if (!user || !chatId) return setActiveLastReadAt(null);
      try {
        const rs = await getDoc(doc(db, "chats", chatId, "reads", user.uid));
        setActiveLastReadAt(rs.exists() ? rs.data()?.lastReadAt || null : null);
      } catch {
        setActiveLastReadAt(null);
      }
    };
    loadLastRead();
  }, [user?.uid, chatId]);

  useEffect(() => {
    const markRead = async () => {
      if (!user || !chatId) return;
      try {
        await setDoc(
          doc(db, "chats", chatId, "reads", user.uid),
          { lastReadAt: serverTimestamp() },
          { merge: true }
        );
        setActiveLastReadAt(new Date());
      } catch {}
    };
    if (partnerId) markRead();
  }, [user?.uid, chatId, partnerId, messages.length]);

  const sendMessage = async (e) => {
    e?.preventDefault?.();
    if (!user || !partnerId || !chatId) return;
    if (!newMessage.trim() && !file) return;

    const msgsRef = collection(db, "chats", chatId, "messages");
    const baseMsg = {
      text: newMessage.trim(),
      from: user.uid,
      to: partnerId,
      createdAt: serverTimestamp(),
      fromRole: userRole,
    };

    if (file) {
      setUploading(true);
      const storageRef = ref(storage, `chatFiles/${chatId}/${Date.now()}_${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);
      uploadTask.on(
        "state_changed",
        null,
        (err) => {
          console.error(err);
          setUploading(false);
        },
        async () => {
          const url = await getDownloadURL(storageRef);
          await addDoc(msgsRef, {
            ...baseMsg,
            fileUrl: url,
            fileType: file.type?.startsWith("image") ? "image" : "file",
          });
          setFile(null);
          setUploading(false);
          if (newMessage.trim()) {
            await addDoc(msgsRef, { ...baseMsg, fileUrl: null, fileType: null });
          }
          setNewMessage("");
          setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
        }
      );
    } else {
      await addDoc(msgsRef, { ...baseMsg, fileUrl: null, fileType: null });
      setNewMessage("");
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    }
  };

  const handleFileSelect = (e) => setFile(e.target.files[0]);

  useEffect(() => {
    const pickMostRecentContact = async () => {
      if (!user || partnerId) return;
      const contactsRef = collection(db, "contacts");
      const [fSnap, iSnap] = await Promise.all([
        getDocs(query(contactsRef, where("founderId", "==", user.uid))),
        getDocs(query(contactsRef, where("investorId", "==", user.uid))),
      ]);
      const all = [...fSnap.docs, ...iSnap.docs]
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.startedAt?.toMillis?.() || 0) - (a.startedAt?.toMillis?.() || 0));
      if (all.length > 0) {
        const top = all[0];
        const other = top.founderId === user.uid ? top.investorId : top.founderId;
        if (other) navigate(`/inbox?user=${other}`, { replace: true });
      }
    };
    pickMostRecentContact();
  }, [user?.uid, partnerId, navigate]);

  useEffect(() => {
    if (!user) return;
    const contactsRef = collection(db, "contacts");
    const qFounder = query(contactsRef, where("founderId", "==", user.uid));
    const qInvestor = query(contactsRef, where("investorId", "==", user.uid));

    let founderDocs = [];
    let investorDocs = [];

    const rebuild = async () => {
      const allDocs = [...founderDocs, ...investorDocs];
      const mapByChat = new Map();

      allDocs.forEach((d) => {
        const data = d.data();
        const otherId = data.founderId === user.uid ? data.investorId : data.founderId;
        if (!otherId) return;
        const cId = [user.uid, otherId].sort().join("_");
        const prev = mapByChat.get(cId);
        const startedAt = data.startedAt;
        if (!prev || (startedAt?.toMillis?.() || 0) > (prev.startedAt?.toMillis?.() || 0)) {
          mapByChat.set(cId, { otherId, startedAt });
        }
      });

      for (const [ci] of mapByChat) {
        purgeOldMessages(ci);
      }

      const items = Array.from(mapByChat.values());
      const enriched = await Promise.all(
        items.map(async (it) => {
          const ci = [user.uid, it.otherId].sort().join("_");
          let lastMessage = "";
          let lastTime = "";
          try {
            const snap = await getDocs(
              query(collection(db, "chats", ci, "messages"), orderBy("createdAt", "desc"), limit(1))
            );
            if (!snap.empty) {
              const d = snap.docs[0].data();
              lastMessage = d.text || (d.fileType === "image" ? "📷 Photo" : d.fileType === "file" ? "📎 Attachment" : "");
              const ts = d.createdAt?.toDate ? d.createdAt.toDate() : null;
              lastTime = ts ? ts.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
            }
          } catch {}

          let name = "";
          try {
            const uSnap = await getDoc(doc(db, "users", it.otherId));
            if (uSnap.exists()) {
              const u = uSnap.data();
              name = u.firstName || u.fullName || "";
            }
          } catch {}

          let unreadCount = 0;
          try {
            const readSnap = await getDoc(doc(db, "chats", ci, "reads", user.uid));
            const lastReadAt = readSnap.exists() ? readSnap.data()?.lastReadAt || null : null;
            const rSnap = await getDocs(
              query(collection(db, "chats", ci, "messages"), orderBy("createdAt", "desc"), limit(50))
            );
            unreadCount = rSnap.docs.reduce((acc, d) => {
              const data = d.data();
              const ts = data.createdAt?.toDate ? data.createdAt.toDate() : null;
              if (data.to === user.uid && (!lastReadAt || (ts && ts > lastReadAt))) return acc + 1;
              return acc;
            }, 0);
          } catch {}

          return { ...it, name, lastMessage, lastTime, unreadCount };
        })
      );

      enriched.sort((a, b) => (b.startedAt?.toMillis?.() || 0) - (a.startedAt?.toMillis?.() || 0));
      setChatList(enriched);
      setTotalUnread(enriched.reduce((sum, it) => sum + (it.unreadCount || 0), 0));
    };

    const unsubF = onSnapshot(qFounder, (snap) => {
      founderDocs = snap.docs;
      rebuild();
    });
    const unsubI = onSnapshot(qInvestor, (snap) => {
      investorDocs = snap.docs;
      rebuild();
    });

    return () => {
      unsubF();
      unsubI();
    };
  }, [user?.uid]);

  // Sidebar Filter Logic based on user's query input
  const filteredChatList = useMemo(() => {
    return chatList.filter((item) => {
      const targetName = (item.name || item.otherId || "").toLowerCase();
      return targetName.includes(searchQuery.toLowerCase());
    });
  }, [chatList, searchQuery]);

  const groupedMessages = useMemo(() => {
    const out = [];
    let prevDay = null;
    for (const m of messages) {
      const ts = m.createdAt?.toDate ? m.createdAt.toDate() : null;
      const dayKey = ts ? dayjs(ts).format("YYYY-MM-DD") : "unknown";
      if (dayKey !== prevDay) {
        out.push({ __type: "date", ts: ts || new Date() });
        prevDay = dayKey;
      }
      out.push({ __type: "msg", data: m });
    }
    return out;
  }, [messages]);

  if (!user)
    return (
      <div className="flex justify-center items-center h-full text-gray-500 font-medium">
        ⚠️ Session expired. Please log in again.
      </div>
    );

  return (
    <div className="flex h-[calc(100vh-32px)] bg-[#f0f2f5] p-0 rounded-none overflow-hidden max-w-[1600px] mx-auto shadow-xl border border-gray-200/50">
      
      {/* 1. WHATSAPP SIDEBAR */}
      <aside className="w-[380px] bg-white border-r border-gray-200 flex flex-col flex-shrink-0">
        
        {/* Sidebar Header Profile Layer */}
        <div className="bg-[#f0f2f5] px-4 py-3 flex items-center justify-between border-b border-gray-200">
          <div className="h-10 w-10 rounded-full bg-emerald-700 text-white font-bold flex items-center justify-center text-sm shadow-sm select-none">
            {userRole ? userRole.charAt(0).toUpperCase() : "U"}
          </div>
          <div className="flex items-center space-x-3">
            {totalUnread > 0 && (
              <span className="inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full bg-emerald-500 text-white text-[11px] font-bold shadow-sm animate-pulse">
                {totalUnread} New
              </span>
            )}
            <span className="text-gray-500 text-lg cursor-default select-none" title="Global Communications Portal">💬</span>
          </div>
        </div>

        {/* WhatsApp-Style Search Input Container */}
        <div className="p-2 bg-white border-b border-gray-100">
          <div className="bg-[#f0f2f5] flex items-center px-3 py-1.5 rounded-lg border border-transparent focus-within:border-gray-200 transition-all">
            <span className="text-gray-400 mr-3 text-xs select-none">🔍</span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search or start a new conversation..."
              className="bg-transparent text-sm w-full outline-none text-gray-700 placeholder-gray-400"
            />
          </div>
        </div>

        {/* Filtered Conversation Scrollway */}
        <div className="flex-1 overflow-y-auto bg-white divide-y divide-gray-50">
          {filteredChatList.length === 0 ? (
            <div className="p-6 text-center text-xs text-gray-400 font-medium">
              No matching profiles found
            </div>
          ) : (
            filteredChatList.map((item) => {
              const isSelected = partnerId === item.otherId;
              return (
                <button
                  key={item.otherId}
                  onClick={() => navigate(`/inbox?user=${item.otherId}`)}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-all ${
                    isSelected ? "bg-[#f0f2f5]" : "hover:bg-gray-50"
                  }`}
                >
                  {/* Account Avatar with Online/Offline styling layout anchor */}
                  <div className="relative flex-shrink-0">
                    <div className="h-11 w-11 rounded-full bg-emerald-600/10 text-emerald-800 border border-emerald-600/20 flex items-center justify-center text-sm font-bold shadow-inner">
                      {(item.name || "?").slice(0, 2).toUpperCase()}
                    </div>
                    {item.unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 block h-3 w-3 rounded-full ring-2 ring-white bg-emerald-500 animate-ping" />
                    )}
                  </div>

                  {/* Metadata Meta Fields */}
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-baseline mb-0.5">
                      <span className={`text-sm truncate text-gray-900 ${item.unreadCount > 0 ? "font-bold" : "font-semibold"}`}>
                        {item.name || "Global Investor / Founder"}
                      </span>
                      <span className={`text-[10px] whitespace-nowrap ${item.unreadCount > 0 ? "text-emerald-600 font-bold" : "text-gray-400"}`}>
                        {item.lastTime || ""}
                      </span>
                    </div>
                    <p className={`text-xs truncate ${item.unreadCount > 0 ? "text-gray-900 font-medium" : "text-gray-400"}`}>
                      {item.lastMessage || "Secure end-to-end communications linked."}
                    </p>
                  </div>

                  {/* Unread Counter Pill Badge */}
                  {item.unreadCount > 0 && (
                    <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-emerald-500 text-white text-[10px] font-black shadow-sm">
                      {item.unreadCount}
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>
      </aside>

      {/* 2. CHAT CHANNELS SECTION */}
      <section className="flex-1 flex flex-col bg-[#efeae2] relative">
        {/* Authentic Wallpaper Mesh Layer Overlay */}
        <div className="absolute inset-0 opacity-[0.35] pointer-events-none bg-[radial-gradient(#d1c7bd_1.2px,transparent_1.2px)] [background-size:20px_20px] z-0"></div>

        {partnerId ? (
          <>
            {/* Header Module */}
            <div className="z-10 px-4 py-2.5 bg-[#f0f2f5] border-b border-gray-200 flex items-center justify-between shadow-sm">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-emerald-700 text-white font-bold flex items-center justify-center text-xs shadow-inner">
                  {(partnerName || "?").slice(0, 2).toUpperCase()}
                </div>
                <div className="flex flex-col">
                  <div className="text-[14.5px] font-bold text-gray-800 leading-tight">
                    {partnerName || "Encrypted Identity Connection"}
                  </div>
                  <div className="text-[11px] text-gray-400 font-medium">
                    Verified Connection • Cloud Data Privacy Cycle Active
                  </div>
                </div>
              </div>
              <div className="text-xs font-semibold px-2.5 py-1 rounded bg-black/5 text-gray-500 uppercase tracking-wider select-none">
                30D Auto-Clear
              </div>
            </div>

            {/* Core Continuous Stream Timeline */}
            <div className="z-10 flex-1 overflow-y-auto p-4 space-y-1 bg-transparent">
              {groupedMessages.map((entry, idx) => {
                if (entry.__type === "date") {
                  return <DateChip ts={entry.ts} key={`d_${idx}`} />;
                }
                const msg = entry.data;
                const isMine = msg.from === user.uid;
                const ts = msg.createdAt?.toDate ? msg.createdAt.toDate() : null;
                const time = ts ? ts.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
                const unread = !isMine && (activeLastReadAt ? ts && ts > activeLastReadAt : true);

                return (
                  <ChatBubble
                    key={msg.id}
                    msg={msg}
                    isMine={isMine}
                    time={time}
                    unread={unread}
                  />
                );
              })}
              <div ref={messagesEndRef}></div>
            </div>

            {/* Input Dashboard Dock */}
            <form onSubmit={sendMessage} className="z-10 p-2.5 bg-[#f0f2f5] flex items-center gap-3 border-t border-gray-200/80 shadow-md">
              
              {/* Media File Attachment Access Pin */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className={`flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-200/80 text-lg transition-all active:scale-95 ${
                  file ? "bg-emerald-100 text-emerald-700 font-bold" : "text-gray-500"
                }`}
                title={file ? `Selected file: ${file.name}` : "Attach dynamic documents or pitch screenshots"}
              >
                {file ? "✓" : "📎"}
              </button>
              <input 
                type="file" 
                ref={fileInputRef} 
                onChange={handleFileSelect} 
                className="hidden" 
                id="fileInput" 
              />

              {/* Functional Messaging Input Container */}
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={uploading ? "Securing cloud database attachment pipelines..." : "Type a message..."}
                disabled={uploading}
                className="flex-1 bg-white border border-transparent rounded-lg px-4 py-2 text-[14px] text-gray-700 outline-none shadow-sm focus:border-black/5 transition-all placeholder-gray-400"
              />
              
              {/* Primary Dispatch Module */}
              <button
                type="submit"
                disabled={uploading || (!newMessage.trim() && !file)}
                className="flex-shrink-0 w-10 h-10 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white rounded-full flex items-center justify-center transition-all shadow-md active:scale-90"
              >
                ✈️
              </button>
            </form>
          </>
        ) : (
          /* Empty Active Panel Standby Screen */
          <div className="z-10 flex-1 flex flex-col justify-center items-center text-gray-400 p-6 selection:bg-transparent">
            <div className="max-w-md text-center bg-white/80 backdrop-blur-sm border border-gray-200/40 p-8 rounded-2xl shadow-xl">
              <div className="text-4xl mb-4 select-none animate-bounce">💼</div>
              <h3 className="text-base font-bold text-gray-800 mb-2">InvestorLanes Communication Suite</h3>
              <p className="text-xs text-gray-500 leading-relaxed mb-4">
                Select an international investor or startup founder from the left panel sidebar matrix to open direct communication channels.
              </p>
              <div className="text-[11px] text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-lg inline-block font-semibold">
                🔒 Privacy Secured: Threads clear systematically every 30 days.
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default Inbox;
